import { useCallback } from 'react';

import { pdf } from '@react-pdf/renderer';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionQRCode } from 'src/app/shared/hooks/qrcode';

import type { Context, Petition, Template } from '../../types';
import { useFlyerImages } from '../useFlyerImages';
import { useFlyerShare } from '../useFlyerShare';

import { FlyerBPDF } from './components/FlyerBPDF';
import { usePetitionTitleImage } from './hooks/usePetitionTitleImage';

type UseFlyerBArgs = { petition: Petition; context: Context };
export function useFlyerB({ petition, context }: UseFlyerBArgs): Template {
	const { translate } = useI18n();

	const share = useFlyerShare({ petition, context, content: 'flyer-b' });
	const shareURL = share.shortUrl ?? share.url;

	// qrcode size is calculated by multiplying the width in the PDF by 3 (for 300DPI)
	const qrCodeState = usePetitionQRCode({
		slugOrId: String(petition.id),
		type: 'WEB_STARTER_DASHBOARD_FLYER_B',
	});
	const qrCode = qrCodeState.data?.imageBase64Uri ?? '';

	const petitionTitleState = usePetitionTitleImage({ id: petition.id, title: petition.ask });
	const petitionTitle = petitionTitleState.data ?? '';

	const generatePDF = useCallback(async () => {
		const props = {
			petitionTitleDataURL: petitionTitle,
			qrCodeDataURL: qrCode,
			scanToSign: translate('corgi.petition.starter_dashboard.grow_petition.share_petition_offline.flyer_qrcode_label'),
		};
		const blob = pdf(<FlyerBPDF {...props} />).toBlob();

		return blob;
	}, [petitionTitle, qrCode, translate]);

	const filename = translate('corgi.petition.starter_dashboard.shared.flyer.filename', { templateName: 'flyer-b' });

	const images = useFlyerImages('flyer-b');

	return {
		name: 'flyer-b',
		enabled: true,
		images,
		filename,
		shareURL,
		loading: qrCodeState.isPending || petitionTitleState.isPending,
		track: share.track,
		generate: generatePDF,
	};
}
